import React, { useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Tooltip, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./Dashboard.css";
import DijonCard from "./DijonCard";
import { RI_BEACHES, DOG_POLICY, LAYERS, FISH_TYPES } from "./beachesData";

// Color-coded pin built as a divIcon so we don't depend on leaflet's
// default marker image assets (which break under webpack builds).
const makeIcon = (color) =>
  L.divIcon({
    className: "beach-pin",
    html: `<span style="
      display:block;
      width:16px;height:16px;
      background:${color};
      border:2px solid #fff;
      border-radius:50% 50% 50% 0;
      transform:rotate(-45deg);
      box-shadow:0 0 3px rgba(0,0,0,0.5);
    "></span>`,
    iconSize: [16, 16],
    iconAnchor: [8, 16],
    popupAnchor: [0, -16],
  });

const iconCache = Object.fromEntries(
  Object.values(DOG_POLICY).map((p) => [p.key, makeIcon(p.color)])
);
// Pins colored by fish species (used in Fishing-only mode)
const fishIconCache = Object.fromEntries(
  Object.values(FISH_TYPES).map((f) => [f.key, makeIcon(f.color)])
);

function DijonBeaches(props) {
  // Which policy categories are visible (all on by default)
  const [visible, setVisible] = useState(() =>
    Object.values(DOG_POLICY).reduce((acc, p) => ({ ...acc, [p.key]: true }), {})
  );
  // Names off by default (toggle on in the panel); too cluttered otherwise
  const [labels, setLabels] = useState(false);
  // Which layers (spot categories) are visible (all on by default)
  const [layerOn, setLayerOn] = useState(() =>
    Object.values(LAYERS).reduce((acc, l) => ({ ...acc, [l.key]: true }), {})
  );
  // Species filter — only used when Fishing is the sole active layer
  const [fishOn, setFishOn] = useState(() =>
    Object.values(FISH_TYPES).reduce((acc, f) => ({ ...acc, [f.key]: true }), {})
  );
  // Filter panel collapse — starts closed on small (mobile) screens
  const [panelOpen, setPanelOpen] = useState(
    () => typeof window === "undefined" || window.innerWidth > 700
  );

  const toggle = (key) =>
    setVisible((v) => ({ ...v, [key]: !v[key] }));

  const toggleLayer = (key) =>
    setLayerOn((v) => ({ ...v, [key]: !v[key] }));

  const toggleFish = (key) =>
    setFishOn((v) => ({ ...v, [key]: !v[key] }));

  const closeCard = () =>
    props.setActiveCards({ ...props.activeCards, beaches: false });

  // "Fishing-only" mode: Fishing layer on, every other layer off.
  const onlyFishing =
    layerOn.fishing &&
    Object.values(LAYERS).every((l) => l.key === "fishing" || !layerOn[l.key]);

  // Show a spot if its dog policy is enabled AND it belongs to at least one
  // enabled layer. In Fishing-only mode, also filter by selected species.
  const shown = useMemo(
    () =>
      RI_BEACHES.filter((b) => {
        if (!visible[b.dogs]) return false;
        const tags = b.tags && b.tags.length ? b.tags : ["beach"];
        if (!tags.some((t) => layerOn[t])) return false;
        if (onlyFishing && b.fish) return b.fish.some((f) => fishOn[f]);
        return true;
      }),
    [visible, layerOn, onlyFishing, fishOn]
  );

  const legendRow = (p) => (
    <div
      key={p.key}
      onClick={() => toggle(p.key)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        cursor: "pointer",
        opacity: visible[p.key] ? 1 : 0.35,
        marginBottom: "4px",
        userSelect: "none",
      }}
      title="Click to show/hide"
    >
      <span
        style={{
          width: "12px",
          height: "12px",
          borderRadius: "50%",
          background: p.color,
          border: "1px solid #fff",
          flexShrink: 0,
        }}
      />
      <span style={{ fontSize: "12px" }}>{p.label}</span>
    </div>
  );

  return (
    <div id="beaches">
      <DijonCard
        width={"720px"}
        height={"640px"}
        startMaximized={true}
        activeCards={props.activeCards}
        setActiveCards={props.setActiveCards}
        name={"beaches"}
        content={
          <div style={{ height: "100%", width: "100%", position: "relative" }}>
            <MapContainer
              center={[41.58, -71.55]}
              zoom={10}
              scrollWheelZoom={true}
              style={{ height: "100%", width: "100%" }}
            >
              {/* Satellite imagery base + place/road labels overlay (Esri, no key needed) */}
              <TileLayer
                attribution='Imagery &copy; Esri, Maxar, Earthstar Geographics'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                maxZoom={19}
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                maxZoom={19}
              />
              {shown.map((b) => {
                const policy = DOG_POLICY[b.dogs];
                return (
                  <Marker
                    key={b.name}
                    position={[b.lat, b.lng]}
                    icon={
                      onlyFishing && b.fish
                        ? fishIconCache[b.fish[0]]
                        : iconCache[b.dogs]
                    }
                  >
                    {labels && (
                      <Tooltip
                        permanent
                        direction="top"
                        offset={[0, -14]}
                        className="beach-label"
                      >
                        {b.name}
                      </Tooltip>
                    )}
                    <Popup>
                      <strong>{b.name}</strong>
                      <br />
                      {b.town}
                      <br />
                      <span style={{ color: policy.color, fontWeight: 600 }}>
                        {policy.label}
                      </span>
                      {b.tags && b.tags.length > 0 && (
                        <>
                          <br />
                          <span style={{ fontSize: "12px" }}>
                            {b.tags
                              .map((t) => (LAYERS[t] ? LAYERS[t].icon + " " + LAYERS[t].label : ""))
                              .filter(Boolean)
                              .join(" · ")}
                          </span>
                        </>
                      )}
                      {b.fish && b.fish.length > 0 && (
                        <>
                          <br />
                          <span style={{ fontSize: "11px", color: "#333" }}>
                            🎣{" "}
                            {b.fish
                              .map((f) => (FISH_TYPES[f] ? FISH_TYPES[f].label : f))
                              .join(", ")}
                          </span>
                        </>
                      )}
                      {b.note && (
                        <>
                          <br />
                          <span style={{ fontSize: "11px", color: "#555" }}>
                            {b.note}
                          </span>
                        </>
                      )}
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>

            {/* Close button */}
            <button
              onClick={closeCard}
              title="Exit the beach map"
              style={{
                position: "absolute",
                top: "10px",
                right: "10px",
                zIndex: 1001,
                background: "#c62828",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                padding: "6px 12px",
                fontSize: "13px",
                fontWeight: 700,
                cursor: "pointer",
                boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
              }}
            >
              ✕ Exit map
            </button>

            {/* Legend / filter overlay */}
            <div
              style={{
                position: "absolute",
                top: "50px",
                right: "10px",
                zIndex: 1000,
                background: "rgba(255,255,255,0.92)",
                padding: "10px 12px",
                borderRadius: "6px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
                color: "#222",
                maxHeight: "calc(100% - 70px)",
                overflowY: "auto",
                maxWidth: "72vw",
              }}
            >
              {/* Collapsible header — tap to minimize the menu (esp. on mobile) */}
              <div
                onClick={() => setPanelOpen((o) => !o)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "16px",
                  cursor: "pointer",
                  userSelect: "none",
                  fontSize: "13px",
                  fontWeight: 700,
                }}
              >
                <span>☰ Filters</span>
                <span>{panelOpen ? "▾ hide" : "▸ show"}</span>
              </div>
              {panelOpen && (
              <div style={{ marginTop: "8px" }}>
              {/* Animated wave accent (self-contained CSS/SVG — no gif asset) */}
              <style>{`
                @keyframes beachWaveDrift { from { transform: translateX(0); } to { transform: translateX(-50%); } }
              `}</style>
              <div style={{ overflow: "hidden", height: "16px", borderRadius: "4px", marginBottom: "6px", background: "#e3f2fd" }}>
                <svg
                  viewBox="0 0 240 16"
                  preserveAspectRatio="none"
                  style={{ width: "200%", height: "100%", display: "block", animation: "beachWaveDrift 3s linear infinite" }}
                >
                  <path d="M0 10 Q 15 3 30 10 T 60 10 T 90 10 T 120 10 T 150 10 T 180 10 T 210 10 T 240 10 V16 H0 Z" fill="#2196f3" opacity="0.85" />
                  <path d="M0 11 Q 15 5 30 11 T 60 11 T 90 11 T 120 11 T 150 11 T 180 11 T 210 11 T 240 11 V16 H0 Z" fill="#64b5f6" opacity="0.7" />
                </svg>
              </div>
              {/* Layers */}
              <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "6px" }}>
                Layers
              </div>
              {Object.values(LAYERS).map((l) => (
                <div
                  key={l.key}
                  onClick={() => toggleLayer(l.key)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    cursor: "pointer",
                    opacity: layerOn[l.key] ? 1 : 0.35,
                    marginBottom: "4px",
                    userSelect: "none",
                    fontSize: "12px",
                  }}
                  title="Show/hide this layer"
                >
                  <span>{layerOn[l.key] ? "☑" : "☐"}</span>
                  <span>
                    {l.icon} {l.label}
                  </span>
                </div>
              ))}
              <hr style={{ margin: "6px 0", borderColor: "#ddd" }} />
              {onlyFishing ? (
                <>
                  <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "2px" }}>
                    🎣 Fish by species
                  </div>
                  <div style={{ fontSize: "10px", color: "#666", marginBottom: "6px" }}>
                    Pins colored by primary catch
                  </div>
                  {Object.values(FISH_TYPES).map((f) => (
                    <div
                      key={f.key}
                      onClick={() => toggleFish(f.key)}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        cursor: "pointer",
                        opacity: fishOn[f.key] ? 1 : 0.35,
                        marginBottom: "4px",
                        userSelect: "none",
                      }}
                      title="Show/hide this species"
                    >
                      <span
                        style={{
                          width: "12px",
                          height: "12px",
                          borderRadius: "50%",
                          background: f.color,
                          border: "1px solid #fff",
                          flexShrink: 0,
                        }}
                      />
                      <span style={{ fontSize: "12px" }}>{f.label}</span>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div
                    style={{
                      fontSize: "13px",
                      fontWeight: 700,
                      marginBottom: "6px",
                    }}
                  >
                    🐾 Dogs Allowed?
                  </div>
                  {Object.values(DOG_POLICY).map(legendRow)}
                </>
              )}
              <hr style={{ margin: "6px 0", borderColor: "#ddd" }} />
              <div
                onClick={() => setLabels((l) => !l)}
                style={{
                  fontSize: "12px",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                {labels ? "☑" : "☐"} Show names
              </div>
              </div>
              )}
            </div>

            <div
              style={{
                position: "absolute",
                bottom: "6px",
                left: "10px",
                zIndex: 1000,
                fontSize: "10px",
                color: "#666",
                background: "rgba(255,255,255,0.8)",
                padding: "2px 6px",
                borderRadius: "4px",
              }}
            >
              Policies are seasonal &amp; can change — verify before visiting.
            </div>
          </div>
        }
      />
    </div>
  );
}

export default DijonBeaches;
